import React, { useState, useEffect } from 'react';
import { DollarSign } from 'lucide-react';
import { getDB, isFirebaseConfigured } from '../lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { COLLECTIONS } from '../lib/database.types';
import { format, parseISO, isWithinInterval, addMonths, isSameMonth } from 'date-fns';

interface FinancialData {
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  currentMonthInvoiced: number;
  currentMonthPaid: number;
}

export function FinancialSummary() {
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState<FinancialData>({
    totalInvoiced: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    currentMonthInvoiced: 0,
    currentMonthPaid: 0
  });

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      
      if (!isFirebaseConfigured()) {
        // Mock data for development
        setFinancialData({
          totalInvoiced: 125000,
          totalPaid: 87500,
          totalOutstanding: 37500,
          currentMonthInvoiced: 15000,
          currentMonthPaid: 12000
        });
        return;
      }

      // Fetch all cases
      const casesSnapshot = await getDocs(collection(getDB(), COLLECTIONS.CASES));
      const cases = casesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate financial metrics
      let totalInvoiced = 0;
      let totalPaid = 0;
      let currentMonthInvoiced = 0;
      let currentMonthPaid = 0;

      const currentDate = new Date();
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      cases.forEach((case_: any) => {
        const invoicedAmount = case_.invoiced_amount || 0;
        const paidAmount = case_.paid_amount || 0;

        totalInvoiced += invoicedAmount;
        totalPaid += paidAmount;

        // Check if case was updated this month
        const updatedAt = case_.updated_at?.toDate ? case_.updated_at.toDate() : new Date(case_.updated_at || 0);
        if (updatedAt >= startOfMonth && updatedAt <= endOfMonth) {
          currentMonthInvoiced += invoicedAmount;
          currentMonthPaid += paidAmount;
        }
      });

      setFinancialData({
        totalInvoiced,
        totalPaid,
        totalOutstanding: totalInvoiced - totalPaid,
        currentMonthInvoiced,
        currentMonthPaid
      });
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const metrics = [
    {
      title: 'Total Invoiced',
      value: financialData.totalInvoiced,
      icon: DollarSign,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    },
    {
      title: 'Total Paid',
      value: financialData.totalPaid,
      icon: DollarSign,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'Outstanding',
      value: financialData.totalOutstanding,
      icon: DollarSign,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600'
    },
    {
      title: 'This Month Invoiced',
      value: financialData.currentMonthInvoiced,
      icon: DollarSign,
      color: 'bg-purple-500',
      textColor: 'text-purple-600'
    },
    {
      title: 'This Month Paid',
      value: financialData.currentMonthPaid,
      icon: DollarSign,
      color: 'bg-indigo-500',
      textColor: 'text-indigo-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Financial Summary</h1>
        <button
          onClick={fetchFinancialData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-white overflow-hidden shadow-sm rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`${metric.color} p-3 rounded-md`}>
                    <metric.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      {metric.title}
                    </dt>
                    <dd className={`text-lg font-medium ${metric.textColor}`}>
                      ${metric.value.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Collection Rate */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Collection Performance</h3>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Collection Rate</span>
              <span>
                {financialData.totalInvoiced > 0 
                  ? ((financialData.totalPaid / financialData.totalInvoiced) * 100).toFixed(1)
                  : 0}%
              </span>
            </div>
            <div className="mt-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full"
                style={{
                  width: `${Math.min(100, financialData.totalInvoiced > 0 ? (financialData.totalPaid / financialData.totalInvoiced) * 100 : 0)}%`
                }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                ${financialData.totalPaid.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Collected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                ${financialData.totalOutstanding.toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Outstanding</div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Performance */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Monthly Performance - {format(new Date(), 'MMMM yyyy')}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              ${financialData.currentMonthInvoiced.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Invoiced This Month</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              ${financialData.currentMonthPaid.toLocaleString()}
            </div>
            <div className="text-sm text-gray-500">Paid This Month</div>
          </div>
        </div>
      </div>
    </div>
  );
} 